import { useQuery } from '@apollo/client'
import { AudienceCriterionState } from '../../data/gql-gen/questionnaire/graphql'
import {
  GET_MARKET_AUDIENCE_IDS,
  MarketAudienceIdsData,
  MarketAudienceMemberIds
} from '../../data/gql/questionnaire/queries/marketAudienceIds'
import { LoggerErrorType } from '../../data/logger'
import {
  FieldworkAudienceType,
  MemberSettingCode
} from '../../data/model/audience'
import {
  DraftQuestionnaireEntry,
  EntryType,
  QuestionKind
} from '../../data/model/questionnaire'
import { useSurveyId } from '../../hooks/useSurveyId'
import { captureApolloError } from '../../utils/HelperFunctions'
import {
  getEffectiveCriterionOptions,
  getIsEnabledInSettings
} from '../Audience/utils'
import { useProjectType } from '../Project/Project.hooks'

// Multi-market projects author an audience per market. Display logic lives on
// the shared questionnaire and is stored as plain (questionLk, responseOptionLk)
// ids, so a rule only works everywhere if the question AND the option it points
// at carry the same ids in every market. Matching is therefore on ids alone:
// question text is stored per market in that market's own language, so it says
// nothing about whether two markets mean the same question.

interface AudienceMemberIds {
  questionId: string
  optionIds: string[]
  /** Catalogue name of a standard criterion — the same in every market. */
  criterionName?: string
}

/**
 * The question id and effective option ids a market's audience member exposes,
 * or undefined when that market never asks it — a disabled custom question, or
 * a standard criterion hidden from the survey — since respondents there could
 * never satisfy a rule referencing it.
 */
const memberIds = (
  member: MarketAudienceMemberIds
): AudienceMemberIds | undefined => {
  const item = member.memberItem
  if (member.memberType === FieldworkAudienceType.Custom) {
    if (!item.questionLk || item.state === AudienceCriterionState.Disabled) {
      return undefined
    }
    return {
      questionId: item.questionLk,
      optionIds: (item.criterionOptions ?? []).map(
        (option) => option.responseOptionLk
      )
    }
  }

  const questionId = item.criterion?.question.questionId
  if (
    !questionId ||
    getIsEnabledInSettings(
      item.settingValues ?? [],
      MemberSettingCode.HideSurveyQuestion
    )
  ) {
    return undefined
  }
  const options = getEffectiveCriterionOptions(item)
  return {
    questionId,
    optionIds: options.map(
      (option) => option.criterionOption.responseOption.responseOptionId
    ),
    criterionName: item.criterion?.name
  }
}

const marketAudienceMap = (
  members: MarketAudienceMemberIds[]
): Map<string, AudienceMemberIds> => {
  const byQuestion = new Map<string, AudienceMemberIds>()
  for (const member of members) {
    const ids = memberIds(member)
    if (ids) {
      byQuestion.set(ids.questionId, ids)
    }
  }
  return byQuestion
}

export interface SharedAudienceQuestions {
  /** Option ids that resolve in every market, per question id. */
  optionIdsByQuestionId: Map<string, Set<string>>
  /**
   * Language-independent label per question id, for standard criteria only —
   * their question text is stored in whichever language the market uses.
   */
  labelByQuestionId: Map<string, string>
}

/**
 * The audience questions display logic may reference: those whose id is present
 * in EVERY market, narrowed to the option ids all of them share. A criterion
 * whose catalogue differs by a band in one country stays usable through the
 * options they have in common; one that shares no option at all (custom age
 * bands mint fresh ids) drops out entirely.
 */
export const sharedAudienceQuestions = (
  marketAudiences: MarketAudienceMemberIds[][]
): SharedAudienceQuestions => {
  const optionIdsByQuestionId = new Map<string, Set<string>>()
  const labelByQuestionId = new Map<string, string>()
  if (marketAudiences.length === 0) {
    return { optionIdsByQuestionId, labelByQuestionId }
  }

  const [first, ...rest] = marketAudiences.map(marketAudienceMap)
  for (const [questionId, member] of first) {
    const shared = member.optionIds.filter((optionId) =>
      rest.every(
        (market) => market.get(questionId)?.optionIds.includes(optionId)
      )
    )
    if (shared.length === 0 || rest.some((market) => !market.has(questionId))) {
      continue
    }
    optionIdsByQuestionId.set(questionId, new Set(shared))
    if (member.criterionName) {
      labelByQuestionId.set(questionId, member.criterionName)
    }
  }

  return { optionIdsByQuestionId, labelByQuestionId }
}

const audienceEntryQuestionLk = (
  entry: DraftQuestionnaireEntry
): string | undefined =>
  entry.entryType === EntryType.QuestionEntryType
    ? entry.entryItem.questionLk
    : undefined

/**
 * `referencedQuestionLks` keeps the question a rule already points at listed, so
 * a pre-existing rule stays visible and editable. Pass only the ids the row
 * being rendered references — anything wider lets a NEW row pick a question that
 * can never match outside its own market.
 */
export const filterMultiMarketPickerEntries = (
  entries: DraftQuestionnaireEntry[],
  sharedQuestionIds: ReadonlySet<string>,
  referencedQuestionLks: ReadonlySet<string> = new Set()
): DraftQuestionnaireEntry[] =>
  entries.filter((entry) => {
    if (entry.questionKind !== QuestionKind.AudienceKind) {
      return true
    }
    const questionLk = audienceEntryQuestionLk(entry)
    return (
      questionLk !== undefined &&
      (sharedQuestionIds.has(questionLk) ||
        referencedQuestionLks.has(questionLk))
    )
  })

/**
 * The response options a picker may offer for an audience question: the ones
 * every market shares. Options only some markets have are dropped, since a rule
 * on them could never match the others. Non-audience questions are untouched,
 * as is a question a rule already references (nothing shared to narrow to).
 */
export const filterMultiMarketResponseOptions = <
  T extends { responseOptionLk: string }
>(
  responseOptions: T[],
  questionLk: string | undefined,
  sharedOptionIds: ReadonlyMap<string, ReadonlySet<string>>
): T[] => {
  const shared = questionLk ? sharedOptionIds.get(questionLk) : undefined
  return shared
    ? responseOptions.filter((option) => shared.has(option.responseOptionLk))
    : responseOptions
}

// Whether display logic has any audience question left to reference — the first
// question of a survey can only carry display logic through one.
export const hasReferenceableAudienceQuestion = (
  entries: DraftQuestionnaireEntry[],
  isMultiMarketV2: boolean,
  sharedQuestionIds: ReadonlySet<string>
): boolean =>
  (isMultiMarketV2
    ? filterMultiMarketPickerEntries(entries, sharedQuestionIds)
    : entries
  ).some((entry) => entry.questionKind === QuestionKind.AudienceKind)

/**
 * The audience questions every market shares, for the display-logic pickers.
 * Empty outside multi-market and until the markets resolve — the pickers then
 * offer only what a rule already references, which fails closed rather than
 * offering a question that silently never matches in some market.
 */
export const useSharedAudienceQuestions = ({
  skip = false
}: { skip?: boolean } = {}): {
  isMultiMarketV2: boolean
  sharedAudienceQuestionIds: Set<string>
  sharedAudienceOptionIds: Map<string, Set<string>>
  audienceLabels: Map<string, string>
} => {
  const { isMultiMarketV2 } = useProjectType()
  const surveyId = useSurveyId()
  const { data } = useQuery<MarketAudienceIdsData>(GET_MARKET_AUDIENCE_IDS, {
    context: { clientName: 'questionnaire' },
    variables: { surveyId },
    skip: skip || !isMultiMarketV2,
    // Audience entities normalise by ids shared across markets — caching more
    // than one market's audience would collide with the audience page's cache.
    fetchPolicy: 'no-cache',
    onError: (error) => {
      captureApolloError(
        LoggerErrorType.ApolloQuery,
        'marketAudienceIds',
        error
      )
    }
  })

  const markets = data?.questionnaireAggV2?.marketsSpecification ?? []
  const { optionIdsByQuestionId, labelByQuestionId } = sharedAudienceQuestions(
    markets.map((market) => market.value.draftAudience.members)
  )

  return {
    isMultiMarketV2,
    sharedAudienceQuestionIds: new Set(optionIdsByQuestionId.keys()),
    sharedAudienceOptionIds: optionIdsByQuestionId,
    audienceLabels: labelByQuestionId
  }
}
